import { createContext, useContext, useEffect, useMemo } from "react";
import brandConfig from "@/config/brand";

const BrandContext = createContext(brandConfig);

export const useBrand = () => useContext(BrandContext);

// "#42714D" -> "66 113 77" so Tailwind alpha modifiers keep working.
// Also accepts "rgb(66, 113, 77)" / "66 113 77" in case brand.js is edited to those forms.
const toChannels = (value) => {
  if (!value) return null;
  const hex = value.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1];
    const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    const n = parseInt(full, 16);
    return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
  }
  const nums = value.match(/\d{1,3}/g);
  return nums && nums.length >= 3 ? nums.slice(0, 3).join(" ") : null;
};

const tokenMap = {
  cream: "cream",
  creamDeep: "cream-deep",
  surface: "surface",
  forest: "forest",
  coralInk: "coral-ink",
  forestDeep: "forest-deep",
  rust: "rust",
  sage: "sage",
  sageSoft: "sage-soft",
  amber: "amber",
  honey: "honey",
  cocoa: "cocoa",
};

export function BrandProvider({ children }) {
  const value = useMemo(() => brandConfig, []);

  useEffect(() => {
    const root = document.documentElement;
    const { colors, fonts } = value;

    Object.entries(tokenMap).forEach(([key, name]) => {
      const hex = colors[key];
      if (!hex) return;
      const channels = toChannels(hex);
      if (!channels) return;
      root.style.setProperty(`--brand-${name}`, hex);
      root.style.setProperty(`--brand-${name}-rgb`, channels);
    });

    root.style.setProperty("--font-heading", fonts.heading);
    root.style.setProperty("--font-body", fonts.body);

    document.title = value.seo.defaultTitle;
    const desc = document.getElementById("app-meta-description");
    if (desc) desc.setAttribute("content", value.seo.description);
    if (value.faviconUrl) {
      const icon = document.getElementById("app-favicon");
      if (icon) icon.setAttribute("href", value.faviconUrl);
    }
  }, [value]);

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export default BrandProvider;
